import {
	Download as DownloadIcon,
	Print as PrintIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import type { Item, Session, User } from "../types";

interface ExportResultsProps {
	session: Session;
	users: Record<string, User>;
	isHost: boolean;
}

export function ExportResults({ session, users, isHost }: ExportResultsProps) {
	const [exportOpen, setExportOpen] = useState(false);
	const printRef = useRef<HTMLDivElement>(null);

	if (!isHost) return null;

	const formatTime = (timestamp: number): string => {
		return new Date(timestamp).toLocaleString();
	};

	const formatJoinTime = (timestamp: number): string => {
		const now = Date.now();
		const seconds = Math.floor((now - timestamp) / 1000);

		if (seconds < 60) return "just now";
		if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
		if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
		return new Date(timestamp).toLocaleDateString();
	};

	const getAuthorNames = (item: Item): string => {
		if (item.authorIds && item.authorIds.length > 1) {
			return item.authorIds
				.map((id: string) => users[id]?.name || "Unknown")
				.join(", ");
		}
		return users[item.authorId]?.name || "Unknown";
	};

	const handlePrint = () => {
		if (printRef.current) {
			// Create a new window for printing
			const printWindow = window.open("", "_blank");
			if (printWindow) {
				printWindow.document.write(`
					<!DOCTYPE html>
					<html>
					<head>
						<title>Retrospective Results - Session ${session.id.slice(0, 8)}</title>
						<style>
							body { 
								font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
								margin: 20px;
								line-height: 1.6;
								color: #333;
							}
							.header { 
								text-align: center; 
								border-bottom: 2px solid #1976d2; 
								padding-bottom: 20px; 
								margin-bottom: 30px;
							}
							.section { 
								margin-bottom: 30px; 
								page-break-inside: avoid;
							}
							.section-title { 
								font-size: 18px; 
								font-weight: bold; 
								color: #1976d2; 
								margin-bottom: 15px;
								border-bottom: 1px solid #e0e0e0;
								padding-bottom: 5px;
							}
							.item { 
								background: #f5f5f5; 
								padding: 12px; 
								margin: 8px 0; 
								border-radius: 4px;
								border-left: 4px solid #1976d2;
							}
							.item-text { 
								font-weight: 500; 
								margin-bottom: 5px;
							}
							.item-meta { 
								font-size: 12px; 
								color: #666; 
								font-style: italic;
							}
							.participant { 
								display: inline-block; 
								background: #e3f2fd; 
								padding: 4px 8px; 
								margin: 2px; 
								border-radius: 12px; 
								font-size: 12px;
							}
							.stats { 
								display: flex; 
								justify-content: space-between; 
								margin-bottom: 20px;
								background: #f8f9fa;
								padding: 15px;
								border-radius: 8px;
							}
							.stat-item { 
								text-align: center;
							}
							.stat-number { 
								font-size: 24px; 
								font-weight: bold; 
								color: #1976d2;
							}
							.stat-label { 
								font-size: 12px; 
								color: #666;
							}
							@media print {
								body { margin: 0; }
								.section { page-break-inside: avoid; }
							}
						</style>
					</head>
					<body>
						${printRef.current.innerHTML}
					</body>
					</html>
				`);
				printWindow.document.close();
				printWindow.focus();
				printWindow.print();
				printWindow.close();
			}
		}
		setExportOpen(false);
	};

	const handleDownload = () => {
		handlePrint(); // Same functionality for now
	};

	return (
		<>
			<Button
				color="inherit"
				startIcon={<DownloadIcon />}
				onClick={() => setExportOpen(true)}
				sx={{
					ml: 1,
					border: "1px solid rgba(255, 255, 255, 0.3)",
					"&:hover": {
						backgroundColor: "rgba(255, 255, 255, 0.1)",
						border: "1px solid rgba(255, 255, 255, 0.5)",
					},
				}}
			>
				Export Results
			</Button>

			<Dialog
				open={exportOpen}
				onClose={() => setExportOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Export Retrospective Results</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Choose how you'd like to export the retrospective results:
					</Typography>

					<Box sx={{ display: "flex", gap: 2, mb: 2 }}>
						<Button
							variant="contained"
							startIcon={<PrintIcon />}
							onClick={handlePrint}
							fullWidth
						>
							Print to PDF
						</Button>
						<Button
							variant="outlined"
							startIcon={<DownloadIcon />}
							onClick={handleDownload}
							fullWidth
						>
							Download PDF
						</Button>
					</Box>

					{/* Hidden content for printing */}
					<div ref={printRef} style={{ display: "none" }}>
						<div className="header">
							<h1>Retrospective Results</h1>
							<h2>Session {session.id.slice(0, 8)}</h2>
							<p>Generated on {formatTime(Date.now())}</p>
						</div>

						<div className="stats">
							<div className="stat-item">
								<div className="stat-number">{session.participants.length}</div>
								<div className="stat-label">Participants</div>
							</div>
							<div className="stat-item">
								<div className="stat-number">
									{
										Object.values(session.items).filter(
											(item) => item.category === "good",
										).length
									}
								</div>
								<div className="stat-label">Good Items</div>
							</div>
							<div className="stat-item">
								<div className="stat-number">
									{
										Object.values(session.items).filter(
											(item) => item.category === "bad",
										).length
									}
								</div>
								<div className="stat-label">Bad Items</div>
							</div>
							<div className="stat-item">
								<div className="stat-number">
									{
										Object.values(session.items).filter(
											(item) => item.votes.length > 0,
										).length
									}
								</div>
								<div className="stat-label">Voted Items</div>
							</div>
						</div>

						<div className="section">
							<div className="section-title">Participants</div>
							{session.participants.map((participantId) => {
								const user = users[participantId];
								if (!user) return null;
								return (
									<span key={participantId} className="participant">
										{user.name}{" "}
										{user.joinedAt && `(${formatJoinTime(user.joinedAt)})`}
									</span>
								);
							})}
						</div>

						<div className="section">
							<div className="section-title">What Went Well</div>
							{Object.values(session.items)
								.filter((item) => item.category === "good")
								.sort((a, b) => b.votes.length - a.votes.length)
								.map((item) => (
									<div key={item.id} className="item">
										<div className="item-text">{item.text}</div>
										<div className="item-meta">
											Author: {getAuthorNames(item)} • Votes:{" "}
											{item.votes.length} • Created:{" "}
											{formatTime(item.createdAt)}
										</div>
									</div>
								))}
						</div>

						<div className="section">
							<div className="section-title">What Could Be Improved</div>
							{Object.values(session.items)
								.filter((item) => item.category === "bad")
								.sort((a, b) => b.votes.length - a.votes.length)
								.map((item) => (
									<div key={item.id} className="item">
										<div className="item-text">{item.text}</div>
										<div className="item-meta">
											Author: {getAuthorNames(item)} • Votes:{" "}
											{item.votes.length} • Created:{" "}
											{formatTime(item.createdAt)}
										</div>
									</div>
								))}
						</div>

						{session.timer && (
							<div className="section">
								<div className="section-title">Timer Information</div>
								<div className="item">
									<div className="item-text">
										{session.timer.label || "Timer"} - Duration:{" "}
										{Math.floor(session.timer.duration / 60)}:
										{(session.timer.duration % 60).toString().padStart(2, "0")}
									</div>
									<div className="item-meta">
										Status: {session.timer.isActive ? "Active" : "Inactive"}
									</div>
								</div>
							</div>
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setExportOpen(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
